import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Trash2, Download, Eye, EyeOff, Mail, CheckCircle } from 'lucide-react';

const PrivacyChoices = () => {
  const [requestSent, setRequestSent] = useState(null);

  const handleRequest = (type) => {
    setRequestSent(type);
    // In a real implementation, this would send an API request
    setTimeout(() => setRequestSent(null), 5000);
  };

  return (
    <div className="min-h-screen bg-yellow-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white py-6 px-4">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center text-white hover:text-yellow-200 mb-4">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-3xl font-bold">Privacy Choices</h1>
          <p className="text-red-100 mt-2">Manage your data and privacy preferences</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        
        {/* Introduction */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <div className="flex items-start">
            <Shield className="w-10 h-10 text-red-600 mr-4 flex-shrink-0" />
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">Your Privacy Matters</h2>
              <p className="text-gray-600 leading-relaxed">
                At The Loo Report, we believe you should have control over your personal information. 
                This page explains your privacy rights and how to exercise them. We are committed to 
                transparency and giving you choices about how your data is used.
              </p>
            </div>
          </div>
        </div>

        {/* Privacy Options */}
        <div className="space-y-6">
          
          {/* Request Data */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <Download className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-800 mb-2">Request Your Data</h3>
                <p className="text-gray-600 mb-4">
                  You have the right to request a copy of all personal data we have collected about you, 
                  including your account information and review history.
                </p>
                {requestSent === 'download' ? (
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Request submitted! We'll email your data within 30 days.
                  </div>
                ) : (
                  <a 
                    href="mailto:privacy@thelooreport.com?subject=Data Access Request&body=I would like to request a copy of my personal data.%0D%0A%0D%0AAccount Email: [Enter your email]%0D%0A%0D%0AThank you."
                    onClick={() => handleRequest('download')}
                    className="inline-block bg-blue-600 text-white px-6 py-2 rounded-full font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Request My Data
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Delete Data */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-800 mb-2">Delete Your Data</h3>
                <p className="text-gray-600 mb-4">
                  You have the right to request deletion of your account and all associated personal data. 
                  This includes your profile information and all reviews you've submitted. This action is permanent 
                  and cannot be undone.
                </p>
                {requestSent === 'delete' ? (
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Request submitted! We'll process your deletion request within 30 days.
                  </div>
                ) : (
                  <a 
                    href="mailto:privacy@thelooreport.com?subject=Account Deletion Request&body=I would like to request deletion of my account and all associated data.%0D%0A%0D%0AAccount Email: [Enter your email]%0D%0A%0D%0AI understand this action is permanent and cannot be undone.%0D%0A%0D%0AThank you."
                    onClick={() => handleRequest('delete')}
                    className="inline-block bg-red-600 text-white px-6 py-2 rounded-full font-semibold hover:bg-red-700 transition-colors"
                  >
                    Request Account Deletion
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Opt-Out of Data Sharing */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <EyeOff className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-800 mb-2">Opt-Out of Data Sharing</h3>
                <p className="text-gray-600 mb-4">
                  We do not sell your personal information to third parties. If you wish to opt-out of 
                  any non-essential data processing or limit how your information is used, you can 
                  submit a request below.
                </p>
                {requestSent === 'optout' ? (
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Request submitted! We'll update your preferences within 10 business days.
                  </div>
                ) : (
                  <a 
                    href="mailto:privacy@thelooreport.com?subject=Opt-Out Request&body=I would like to opt-out of non-essential data processing.%0D%0A%0D%0AAccount Email: [Enter your email]%0D%0A%0D%0AThank you."
                    onClick={() => handleRequest('optout')}
                    className="inline-block bg-yellow-500 text-white px-6 py-2 rounded-full font-semibold hover:bg-yellow-600 transition-colors"
                  >
                    Submit Opt-Out Request
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Review Visibility */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <Eye className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-800 mb-2">Review Visibility</h3>
                <p className="text-gray-600 mb-4">
                  Your reviews are public by default to help other users. Your email address is never 
                  displayed publicly. If you wish to have specific reviews removed or made anonymous, 
                  please contact us with the details.
                </p>
                <a 
                  href="mailto:privacy@thelooreport.com?subject=Review Visibility Request&body=I would like to request changes to my review visibility.%0D%0A%0D%0AAccount Email: [Enter your email]%0D%0ARestaurant Name: [Enter restaurant name]%0D%0ARequest: [Remove / Make Anonymous]%0D%0A%0D%0AThank you."
                  className="inline-block bg-purple-600 text-white px-6 py-2 rounded-full font-semibold hover:bg-purple-700 transition-colors"
                >
                  Request Review Changes
                </a>
              </div>
            </div>
          </div>

        </div>

        {/* Your Rights Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mt-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Your Privacy Rights</h2>
          <p className="text-gray-600 mb-4">
            Depending on your location, you may have the following rights under applicable privacy laws 
            (including CCPA, GDPR, and others):
          </p>
          <ul className="space-y-3 text-gray-600">
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <span><strong>Right to Know:</strong> Request information about what personal data we collect and how it's used.</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <span><strong>Right to Access:</strong> Obtain a copy of your personal data.</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <span><strong>Right to Delete:</strong> Request deletion of your personal data.</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <span><strong>Right to Correct:</strong> Request correction of inaccurate personal data.</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <span><strong>Right to Opt-Out:</strong> Opt-out of the sale or sharing of your personal data (we do not sell your data).</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <span><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your privacy rights.</span>
            </li>
          </ul>
        </div>

        {/* Contact Section */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-2xl shadow-lg p-8 mt-6 text-white">
          <div className="flex items-center mb-4">
            <Mail className="w-8 h-8 mr-3" />
            <h2 className="text-2xl font-bold">Contact Us About Privacy</h2>
          </div>
          <p className="text-red-100 mb-6">
            If you have any questions about your privacy choices or need assistance exercising your rights, 
            please contact our privacy team:
          </p>
          <div className="bg-white/10 rounded-xl p-4">
            <p className="text-white">
              <strong>Email:</strong> privacy@thelooreport.com<br />
              <strong>Response Time:</strong> Within 30 days of your request<br />
              <strong>Verification:</strong> We may need to verify your identity before processing requests
            </p>
          </div>
        </div>

        {/* Links */}
        <div className="mt-8 text-center space-x-6">
          <Link to="/privacy" className="text-red-600 hover:text-red-700 font-semibold">
            View Full Privacy Policy
          </Link>
          <Link to="/support" className="text-red-600 hover:text-red-700 font-semibold">
            Support Center
          </Link>
        </div>

      </div>
    </div>
  );
};

export default PrivacyChoices;
